/**
 * Phase 10 - Admin survey results aggregation page.
 * Route: /admin/events/:id/survey-results
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Send, Star } from 'lucide-react';
import api from '@/utils/api';

const fmtStars = (avg) => {
  if (avg == null) return '-';
  return `${avg.toFixed(2)} / 5`;
};

const AdminSurveyResultsPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showText, setShowText] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/events/${id}/survey/results`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const sendSurvey = async () => {
    // Bundle 97 incident fix - the legacy `/survey/send` endpoint
    // emailed real attendees with zero test-mode isolation. Route
    // this button to the Bundle 76+ Survey Console where the admin
    // picks explicitly between "Send test" (surrogate inbox,
    // is_test=True) and "Send live" (guarded fan-out).
    if (!window.confirm(
      'Post-event surveys are managed on the Survey Console. Open it now?',
    )) return;
    window.location.href = `/admin/events/${id}/survey-console`;
  };

  if (loading) return <div className="p-10 text-neutral-500">Loading...</div>;

  const agg = data?.aggregates || {};
  const ratings = ['overall_rating', 'felt_welcomed', 'conversation_quality', 'table_experience'];
  const RATING_LABELS = {
    overall_rating: 'Overall rating',
    felt_welcomed: 'Felt welcomed',
    conversation_quality: 'Conversation quality',
    table_experience: 'Table experience',
  };

  return (
    <div className="p-8 space-y-6 max-w-6xl" data-testid="admin-survey-results">
      <Link to={`/admin/events/${id}/edit`} className="text-sm text-primary-700 hover:text-primary-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to event
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Survey results</h1>
          <p className="text-neutral-600 text-sm">
            {data?.sent_at ? `Sent ${new Date(data.sent_at).toLocaleString()}` : 'Not sent yet.'}
          </p>
        </div>
        <Button onClick={sendSurvey} disabled={sending} className="bg-primary-800 hover:bg-primary-900 text-white" data-testid="survey-send-btn">
          <Send className="h-4 w-4 mr-1.5" /> {data?.sent_at ? 'Send to newcomers' : 'Send survey'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Responses" value={data?.response_count ?? 0} />
        <MetricCard label="Invites sent" value={data?.sent_count ?? 0} />
        <MetricCard label="Response rate" value={`${data?.response_rate ?? 0}%`} accent />
      </div>

      <Card className="border"><CardContent className="p-6 space-y-4">
        <h2 className="font-display text-lg font-bold text-primary-900">Averages (1-5)</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {ratings.map((k) => (
            <div key={k} className="flex items-center justify-between border-b border-neutral-100 pb-2" data-testid={`agg-${k}`}>
              <div className="text-sm text-neutral-700">{RATING_LABELS[k]}</div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-secondary-500 fill-secondary-500" />
                <div className="font-mono font-semibold text-primary-900">{fmtStars(agg[k]?.avg)}</div>
                <span className="text-xs text-neutral-400">({agg[k]?.count || 0})</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card className="border"><CardContent className="p-6 space-y-4">
        <h2 className="font-display text-lg font-bold text-primary-900">Yes / No / Maybe</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <ChoiceDist title="Met someone to stay in touch with" buckets={agg.met_someone_to_stay_in_touch || {}} />
          <ChoiceDist title="Would attend another event" buckets={agg.would_attend_another || {}} />
        </div>
      </CardContent></Card>

      {data?.intro_outcomes && data.intro_outcomes.total_intros_sent > 0 && (
        <Card className="border border-secondary-200 bg-secondary-50/40" data-testid="intro-outcomes-panel">
          <CardContent className="p-6 space-y-3">
            <div>
              <h2 className="font-display text-lg font-bold text-primary-900">Post-event outcomes from introductions</h2>
              <p className="text-xs text-neutral-500 mt-0.5">
                Tracks which of your approved-and-emailed intros led to someone marking &quot;yes, I met someone to stay in touch with&quot; on the post-event survey.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <OutcomeStat label="Intros sent"     value={data.intro_outcomes.total_intros_sent} />
              <OutcomeStat label="Positive"        value={data.intro_outcomes.positive}         accent="green" />
              <OutcomeStat label="No meet"         value={data.intro_outcomes.no_meet}          accent="red" />
              <OutcomeStat label="No response yet" value={data.intro_outcomes.no_response_yet}  accent="gray" />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border"><CardContent className="p-6 space-y-3">
        <h2 className="font-display text-lg font-bold text-primary-900">Preferred future event types</h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries(agg.preferred_future_types || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <Badge key={k} variant="outline" className="capitalize bg-secondary-50 border-secondary-200 text-primary-900">
              {k} <span className="ml-1 font-mono text-secondary-800">×{v}</span>
            </Badge>
          ))}
          {Object.keys(agg.preferred_future_types || {}).length === 0 && (
            <div className="text-sm text-neutral-500">No responses yet.</div>
          )}
        </div>
      </CardContent></Card>

      <Card className="border"><CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-primary-900">Free-text feedback</h2>
          <label className="flex items-center gap-2 text-xs text-neutral-500">
            <input type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} /> Show responses
          </label>
        </div>
        {showText && (
          <div className="space-y-3">
            <FreeText title="Organizer feedback" items={data?.samples?.organizer_feedback || []} />
            <FreeText title="Seating feedback" items={data?.samples?.seating_feedback || []} />
            <FreeText title="Intro feedback" items={data?.samples?.intro_feedback || []} />
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};

const MetricCard = ({ label, value, accent }) => (
  <Card className={`border ${accent ? 'border-secondary-300 bg-secondary-50/60' : ''}`}>
    <CardContent className="p-5">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="text-3xl font-bold text-primary-900 mt-1">{value}</div>
    </CardContent>
  </Card>
);

const OutcomeStat = ({ label, value, accent = null }) => {
  const cls = accent === 'green'
    ? 'text-green-700 border-green-200 bg-green-50'
    : accent === 'red'
    ? 'text-accent-700 border-accent-200 bg-accent-50'
    : accent === 'gray'
    ? 'text-neutral-600 border-neutral-200 bg-neutral-50'
    : 'text-primary-900 border-secondary-200 bg-white';
  return (
    <div className={`border rounded-lg p-3 ${cls}`} data-testid={`outcome-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};



const ChoiceDist = ({ title, buckets }) => {
  const total = Object.values(buckets).reduce((a, b) => a + b, 0) || 0;
  const opts = ['yes', 'no', 'maybe'];
  return (
    <div>
      <div className="text-sm text-neutral-700 mb-2">{title}</div>
      {total === 0 ? (
        <div className="text-xs text-neutral-400">No responses yet.</div>
      ) : (
        <div className="space-y-1">
          {opts.map((o) => {
            const n = buckets[o] || 0;
            const pct = total ? Math.round((n / total) * 100) : 0;
            return (
              <div key={o} className="flex items-center gap-3 text-xs">
                <div className="w-14 capitalize text-neutral-700">{o}</div>
                <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden">
                  <div className="h-2 bg-secondary-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-16 text-right font-mono text-neutral-600">{n} ({pct}%)</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FreeText = ({ title, items }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1">{title}</div>
    {items.length === 0 ? (
      <div className="text-xs text-neutral-400">No responses.</div>
    ) : (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-neutral-800 bg-neutral-50 border border-neutral-100 rounded-md p-3">
            <div className="whitespace-pre-line">{it.text}</div>
            <div className="text-[10px] text-neutral-400 mt-1">{new Date(it.submitted_at).toLocaleString()}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default AdminSurveyResultsPage;
